import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { 
  Phone, Mail, Clock, CheckCircle, 
  RefreshCw, XCircle, Info, PhoneCall,
  Calendar, MessageCircle, Users, Award,
  Headphones, Shield, Globe, HelpCircle
} from 'lucide-react';
import { useContact } from '../context/ContactContext';
import SearchCardNew from '../components/SearchCardNew';
import './AirlineServices.css';

function AirlineServices() {
  const navigate = useNavigate();
  const { contactSettings } = useContact();
  const tfn = contactSettings.tfn || '+1-888-859-0441';
  const email = contactSettings.email || 'support@flightreservationcentre.com';

  const handleSearch = (searchParams) => {
    const queryString = new URLSearchParams(searchParams).toString();
    navigate(`/flight-results?${queryString}`);
  };

  return (
    <>
      <Helmet>
        <title>Customer Service & Support | Flight Reservation Centre Travel Services</title>
        <meta name="description" content="24/7 customer support for flight bookings, changes, cancellations, and travel assistance. Expert help with reservations, ticket modifications, and travel planning." />
        <meta name="keywords" content="customer service, travel support, flight booking help, ticket changes, cancellation assistance, 24/7 support, travel agents" />
        <link rel="canonical" href="https://flightreservationcentre.com/airline-services" />
        
        {/* Open Graph */}
        <meta property="og:title" content="Customer Service & Travel Support" />
        <meta property="og:description" content="24/7 expert travel assistance for all your booking needs" />
        <meta property="og:type" content="website" />
        
        {/* Schema.org markup */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebPage",
            "name": "Customer Service & Travel Support",
            "description": "Professional travel assistance and customer support services",
            "provider": {
              "@type": "Organization",
              "name": "Flight Reservation Centre",
              "contactPoint": {
                "@type": "ContactPoint",
                "telephone": tfn,
                "contactType": "customer service",
                "availableLanguage": "English",
                "areaServed": "US"
              }
            }
          })}
        </script>
      </Helmet>

      <div className="airline-services-page">
        {/* Hero Section */}
        <section className="services-hero">
          <div className="hero-background">
            <img 
              src="https://images.unsplash.com/photo-1556388158-158ea5ccacbd?w=1920&q=80" 
              alt="Airline customer service help" 
              className="hero-bg-image"
            />
            <div className="hero-overlay"></div>
          </div>
          <div className="container">
            <div className="hero-content">
              <h1>Airline Customer Service Help & Travel Support</h1>
              <p className="hero-subtitle">
                Professional airline support assistance for all your travel needs. From flight booking help 
                to airline ticket help, our dedicated airline help desk is here 24/7.
              </p>
              <div className="hero-contact">
                <a href={`tel:${tfn}`} className="hero-cta-primary">
                  <Phone size={24} />
                  <div>
                    <span className="cta-label">Airline Reservation Support</span>
                    <span className="cta-phone">{tfn}</span>
                  </div>
                </a>
                <a href={`mailto:${email}`} className="hero-cta-secondary">
                  <Mail size={24} />
                  <div>
                    <span className="cta-label">Flight Assistance Service</span>
                    <span className="cta-email">{email}</span>
                  </div>
                </a>
              </div>
              <p className="hero-hours">
                <Clock size={18} />
                24/7 Airline Travel Assistance - Expert Help Available Anytime
              </p>
            </div>
          </div>
        </section>

        {/* Flight Search Section */}
        <section className="services-search-section">
          <div className="container">
            <div className="search-section-header">
              <h2>Search & Book Your Flight</h2>
              <p>Find the best deals on flights to destinations worldwide</p>
            </div>
            <SearchCardNew onSearch={handleSearch} />
          </div>
        </section>

        {/* Why Choose Us */}
        <section className="why-choose-us">
          <div className="container">
            <div className="section-header-with-image">
              <div className="section-text">
                <h2>Why Choose Our Airline Reservation Assistance</h2>
                <p className="section-description">
                  Get expert airline travel support from our dedicated team. We provide comprehensive 
                  flight booking help and airline reservation support to make your journey seamless.
                </p>
              </div>
              <div className="section-image">
                <img 
                  src="https://images.unsplash.com/photo-1521791136064-7986c2920216?w=800&q=80" 
                  alt="Airline customer service team providing flight assistance" 
                />
              </div>
            </div>
            
            <div className="benefits-grid">
              <div className="benefit-card">
                <div className="benefit-image">
                  <img 
                    src="https://images.unsplash.com/photo-1423666639041-f56000c27a9a?w=400&q=80" 
                    alt="24/7 airline customer service help"
                  />
                </div>
                <div className="benefit-icon">
                  <Headphones size={32} />
                </div>
                <h3>24/7 Airline Help Desk</h3>
                <p>Real airline customer service help anytime you need it. Our dedicated support team provides immediate assistance for all your travel needs.</p>
              </div>
              
              <div className="benefit-card">
                <div className="benefit-image">
                  <img 
                    src="https://images.unsplash.com/photo-1507679799987-c73779587ccf?w=400&q=80" 
                    alt="Expert airline reservation support agents"
                  />
                </div>
                <div className="benefit-icon">
                  <Award size={32} />
                </div>
                <h3>Expert Airline Travel Assistance</h3>
                <p>Experienced professionals providing airline support assistance and flight booking help to ensure you get the best deals.</p>
              </div>
              
              <div className="benefit-card">
                <div className="benefit-image">
                  <img 
                    src="https://images.unsplash.com/photo-1563013544-824ae1b704d3?w=400&q=80" 
                    alt="Secure airline ticket help and booking"
                  />
                </div>
                <div className="benefit-icon">
                  <Shield size={32} />
                </div>
                <h3>Secure Flight Assistance Service</h3>
                <p>Your payment and personal information is protected with industry-standard encryption throughout the airline reservation process.</p>
              </div>
              
              <div className="benefit-card">
                <div className="benefit-image">
                  <img 
                    src="https://images.unsplash.com/photo-1488085061387-422e29b40080?w=400&q=80" 
                    alt="Global airline travel support network"
                  />
                </div>
                <div className="benefit-icon">
                  <Globe size={32} />
                </div>
                <h3>Global Airline Reservation Assistance</h3>
                <p>Book flights worldwide with our comprehensive airline travel support covering hundreds of airlines and international routes.</p>
              </div>
            </div>
          </div>
        </section>

        {/* Contact Methods */}
        <section className="contact-methods">
          <div className="container">
            <h2>Get Airline Customer Service Help</h2>
            <p className="section-subtitle">Multiple ways to reach our airline support assistance team</p>
            
            <div className="methods-grid">
              <div className="method-card featured">
                <div className="method-bg">
                  <img 
                    src="https://images.unsplash.com/photo-1553877522-43269d4ea984?w=600&q=80" 
                    alt="Phone airline reservation support"
                  />
                  <div className="method-overlay"></div>
                </div>
                <Phone size={48} />
                <h3>Airline Reservation Support</h3>
                <p>Get immediate flight booking help. Speak directly with our airline travel assistance experts.</p>
                <a href={`tel:${tfn}`} className="method-link">{tfn}</a>
                <span className="method-hours">Available 24/7</span>
              </div>
              
              <div className="method-card">
                <div className="method-bg">
                  <img 
                    src="https://images.unsplash.com/photo-1596526131083-e8c633c948d2?w=600&q=80" 
                    alt="Email airline ticket help"
                  />
                  <div className="method-overlay"></div>
                </div>
                <Mail size={48} />
                <h3>Email Airline Support</h3>
                <p>Send your airline ticket help requests and flight assistance service inquiries.</p>
                <a href={`mailto:${email}`} className="method-link">{email}</a>
                <span className="method-hours">Response within 24hrs</span>
              </div>
              
              <div className="method-card">
                <div className="method-bg">
                  <img 
                    src="https://images.unsplash.com/photo-1573497620053-ea5300f94f21?w=600&q=80" 
                    alt="Live chat airline help desk"
                  />
                  <div className="method-overlay"></div>
                </div>
                <MessageCircle size={48} />
                <h3>Live Airline Help Desk</h3>
                <p>Chat in real-time for quick airline reservation assistance and travel support.</p>
                <button className="method-link" onClick={() => window.location.href = '/contact'}>Start Chat</button>
                <span className="method-hours">Available 9am-9pm EST</span>
              </div>
            </div>
          </div>
        </section>

        {/* Services Information */}
        <section className="services-info">
          <div className="container">
            <h2>Complete Airline Travel Support Services</h2>
            <p className="section-subtitle">From flight booking help to airline ticket help, we've got you covered</p>
            
            <div className="services-grid">
              {/* Flight Bookings */}
              <div className="service-card">
                <div className="service-card-image">
                  <img 
                    src="https://images.unsplash.com/photo-1436491865332-7a61a109cc05?w=800&q=80" 
                    alt="Flight booking help and airline reservation support"
                  />
                </div>
                <div className="service-icon">
                  <Calendar size={32} />
                </div>
                <h3>Flight Booking Help</h3>
                <p>
                  Expert airline reservation support for booking flights worldwide. Our airline help desk provides assistance with finding the best fares and routes.
                </p>
                <ul className="policy-list">
                  <li><CheckCircle size={16} /> Professional airline reservation assistance</li>
                  <li><CheckCircle size={16} /> Flexible date search with flight assistance service</li>
                  <li><CheckCircle size={16} /> One-way, round-trip, and multi-city airline support</li>
                  <li><CheckCircle size={16} /> Instant confirmation with airline ticket help</li>
                </ul>
                <div className="policy-note">
                  <Info size={16} />
                  <span>Dedicated airline customer service help for all bookings</span>
                </div>
              </div>

              {/* Flight Changes */}
              <div className="service-card">
                <div className="service-card-image">
                  <img 
                    src="https://images.unsplash.com/photo-1542296332-2e4473faf563?w=800&q=80" 
                    alt="Airline travel assistance for flight changes"
                  />
                </div>
                <div className="service-icon">
                  <RefreshCw size={32} />
                </div>
                <h3>Airline Support Assistance</h3>
                <p>
                  Get airline travel assistance for flight modifications. Our flight assistance service team handles changes with minimal hassle.
                </p>
                <ul className="policy-list">
                  <li><CheckCircle size={16} /> Date and time change airline support</li>
                  <li><CheckCircle size={16} /> Route modification airline reservation assistance</li>
                  <li><CheckCircle size={16} /> Name correction airline ticket help</li>
                  <li><CheckCircle size={16} /> Seat upgrades with airline travel support</li>
                </ul>
                <div className="policy-note">
                  <Info size={16} />
                  <span>Our airline help desk handles all modification requests</span>
                </div>
              </div>

              {/* Cancellations */}
              <div className="service-card">
                <div className="service-card-image">
                  <img 
                    src="https://images.unsplash.com/photo-1517245386807-bb43f82c33c4?w=800&q=80" 
                    alt="Airline reservation support for cancellations"
                  />
                </div>
                <div className="service-icon">
                  <XCircle size={32} />
                </div>
                <h3>Cancellation Airline Customer Service Help</h3>
                <p>
                  Professional airline support assistance for cancellations and refunds. Our airline travel support team maximizes your refund potential.
                </p>
                <ul className="policy-list">
                  <li><CheckCircle size={16} /> 24-hour free cancellation flight assistance</li>
                  <li><CheckCircle size={16} /> Refund processing airline reservation support</li>
                  <li><CheckCircle size={16} /> Travel credit airline ticket help</li>
                  <li><CheckCircle size={16} /> Insurance claim flight assistance service</li>
                </ul>
                <div className="policy-note">
                  <Info size={16} />
                  <span>Expert airline reservation assistance for all cancellations</span>
                </div>
              </div>

              {/* Customer Support */}
              <div className="service-card">
                <div className="service-card-image">
                  <img 
                    src="https://images.unsplash.com/photo-1521737604893-d14cc237f11d?w=800&q=80" 
                    alt="Dedicated airline help desk team"
                  />
                </div>
                <div className="service-icon">
                  <Users size={32} />
                </div>
                <h3>Dedicated Airline Travel Assistance</h3>
                <p>
                  Comprehensive airline customer service help from experienced travel advisors. Get airline support assistance before, during, and after your trip.
                </p>
                <ul className="policy-list">
                  <li><CheckCircle size={16} /> Pre-travel airline reservation assistance</li>
                  <li><CheckCircle size={16} /> Real-time flight status airline travel support</li>
                  <li><CheckCircle size={16} /> Emergency airline ticket help</li>
                  <li><CheckCircle size={16} /> Special requests flight assistance service</li>
                </ul>
                <div className="policy-note">
                  <Info size={16} />
                  <span>24/7 airline help desk available in English</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* FAQs */}
        <section className="faqs-section">
          <div className="container">
            <h2>Frequently Asked Questions</h2>
            
            <div className="faq-grid">
              <div className="faq-item">
                <HelpCircle size={20} />
                <div>
                  <h4>How do I book a flight with Flight Reservation Centre?</h4>
                  <p>
                    You can search for flights on our website, select your preferred option, and complete the booking online. 
                    Alternatively, call our travel experts at {tfn} for personalized assistance with your reservation.
                  </p>
                </div>
              </div>

              <div className="faq-item">
                <HelpCircle size={20} />
                <div>
                  <h4>Can I change or cancel my booking?</h4>
                  <p>
                    Yes! Most tickets can be changed or cancelled. The fees and refund eligibility depend on your ticket type 
                    and the airline's policy. Contact us at {tfn} and we'll help you with the process.
                  </p>
                </div>
              </div>

              <div className="faq-item">
                <HelpCircle size={20} />
                <div>
                  <h4>What is your 24-hour cancellation policy?</h4>
                  <p>
                    Most bookings can be cancelled within 24 hours of purchase for a full refund, as long as the flight 
                    is at least 7 days away. This applies to tickets booked directly with us.
                  </p>
                </div>
              </div>

              <div className="faq-item">
                <HelpCircle size={20} />
                <div>
                  <h4>How quickly will I receive my tickets?</h4>
                  <p>
                    E-tickets are typically sent within minutes of booking confirmation. Check your email (including spam folder) 
                    for your confirmation and ticket details. Need help? Call us at {tfn}.
                  </p>
                </div>
              </div>

              <div className="faq-item">
                <HelpCircle size={20} />
                <div>
                  <h4>Do you offer travel insurance?</h4>
                  <p>
                    Yes, we offer comprehensive travel insurance options during the booking process. Insurance can protect 
                    you against unexpected cancellations, medical emergencies, and more.
                  </p>
                </div>
              </div>

              <div className="faq-item">
                <HelpCircle size={20} />
                <div>
                  <h4>Is my payment information secure?</h4>
                  <p>
                    Absolutely. We use industry-standard SSL encryption and secure payment processing. Your financial 
                    information is never stored on our servers and is protected throughout the transaction.
                  </p>
                </div>
              </div>

              <div className="faq-item">
                <HelpCircle size={20} />
                <div>
                  <h4>Can you help with group bookings?</h4>
                  <p>
                    Yes! We specialize in group travel arrangements. Call {tfn} to speak with our group travel 
                    specialists who can help you get the best rates for 10 or more passengers.
                  </p>
                </div>
              </div>

              <div className="faq-item">
                <HelpCircle size={20} />
                <div>
                  <h4>What if I have special assistance needs?</h4>
                  <p>
                    We can arrange wheelchair assistance, special meals, pet travel, unaccompanied minors, and other 
                    special requests. Contact us at {tfn} to discuss your specific requirements.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Need Help Section */}
        <section className="help-cta">
          <div className="container">
            <div className="cta-card">
              <h2>Ready to Book Your Next Trip?</h2>
              <p>
                Let our experienced travel advisors help you find the perfect flight at the best price. 
                We're available 24/7 to answer your questions and assist with bookings.
              </p>
              <div className="cta-actions">
                <a href={`tel:${tfn}`} className="btn btn-primary">
                  <Phone size={20} />
                  Call {tfn}
                </a>
                <a href="/" className="btn btn-secondary">
                  Search Flights Now
                </a>
              </div>
              <p className="cta-note">
                <Clock size={16} />
                Expert assistance available 24 hours a day, 7 days a week
              </p>
            </div>
          </div>
        </section>

        {/* Disclaimer */}
        <section className="disclaimer">
          <div className="container">
            <div className="disclaimer-content">
              <Info size={20} />
              <div>
                <h4>Important Disclaimer - Airline Customer Service Help</h4>
                <p>
                  <strong>Independent Travel Agency:</strong> Flight Reservation Centre is an independent travel service provider offering airline customer service help, flight booking help, and airline reservation support. We are NOT affiliated with, endorsed by, or directly connected to any airline carrier, airport, or government agency. Our airline support assistance is provided as a third-party service.
                </p>
                <p>
                  <strong>Service Scope:</strong> Our airline travel assistance includes flight assistance service, airline ticket help, and airline reservation assistance. We work with multiple airlines and global distribution systems (GDS) to provide comprehensive airline travel support. Service fees may apply for our airline help desk services in addition to the base ticket price.
                </p>
                <p>
                  <strong>Pricing & Fees:</strong> All flight booking help services display prices that include mandatory taxes and government fees. Additional airline-imposed fees such as baggage fees, seat selection fees, and change fees are subject to individual airline policies. Our airline reservation support team will inform you of all applicable charges before finalizing your booking.
                </p>
                <p>
                  <strong>Terms & Conditions:</strong> All airline ticket help and flight assistance service bookings are subject to availability and the terms and conditions of the respective airline carriers. Each airline maintains its own policies regarding changes, cancellations, refunds, and special services. Our airline customer service help team assists you in understanding and navigating these policies but cannot override airline-specific rules.
                </p>
                <p>
                  <strong>Contact Verification:</strong> The phone number {tfn} is our airline support assistance hotline operated by Flight Reservation Centre staff. This is not a direct airline number. For airline-specific issues like flight status, gate changes, or operational matters, you may need to contact the operating airline directly. Our airline help desk can provide you with appropriate airline contact information when needed.
                </p>
                <p>
                  <strong>Payment Security:</strong> Your financial information is protected using industry-standard SSL encryption. We do not store complete credit card details on our servers. All payment processing for our flight booking help services is handled through secure, PCI-compliant payment gateways.
                </p>
                <p>
                  <strong>No Guarantees:</strong> While our airline travel support team strives to provide the best possible airline reservation assistance, we cannot guarantee specific flight availability, pricing, or outcomes. Airline schedules, routes, and fares are subject to change without notice. Our airline customer service help represents our best efforts to assist you based on available information at the time of booking.
                </p>
                <p className="disclaimer-update">
                  Last Updated: March 2026 | For questions about our airline support assistance services, contact our airline help desk at {tfn}
                </p>
              </div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
}

export default AirlineServices;
